
import {of as observableOf,  Observable, of, Subscription } from 'rxjs';

import {first} from 'rxjs/operators';
import { Injectable, OnDestroy, OnInit } from "@angular/core";

import { GisQueryService } from "../../gis/services/gis-query.service";
import { GisLocationService, CoordinateSystem } from "../../gis/services/gis-location.service";
import { GISConfig, GISOptions } from "../../gis/models/gis-config.model";
import { ContextInteractionService } from "./context-interaction.service";
import { PermitService } from "../../common/services/permit.service";
import { WellConfigurationIndex, WellConfigurationData, WellDirection, StatePlane } from "../components/new-casing-plan/models/casing-plan.model";
import { LoaderService } from "../../common/services/loader.service";
import { isDefined } from "../components/new-casing-plan/models/casing-plan-functions.model";
import { MemoryCache, CacheService } from '../../common/services/cache-service';

@Injectable({
    providedIn: 'root'
})


export class GISControllerService implements OnDestroy {
    aorText = "The proposed wellhead location is within the \"area of review\" for an injection well.  Special conditions will be included with the permit requiring cement to be placed across the zone of active injection.";
    driftThicknessText = "The proposed location has an estimated drift thickness of [[THICKNESS]] feet.  According to Ohio Adm. Code 1501:9-1-08(M)(3)(a)(i), conductor casing shall be set where necessary in order to stabilize unconsolidated sediments.  Alternatively, an operator may forgo setting conductor casing if the surface casing will be drilled on fluid.";
    gasStorageText = "The proposed wellhead location is located within a gas storage reservoir or reservoir protective area, as defined in ORC 1571.01 (A).";
    h2sText = "The proposed location is in a township in which hydrogen sulfide has been encountered in the \"Big Lime\". Ohio Adm.Code 1501:9-1-08 (J)(7) requires sulfate resistant cement be used in such townships in order to protect the casing string and prevent the migration of hydrogen sulfide. The extent of this area may be viewed on our interactive map.";
    lakePlainText = "The proposed wellhead location is within a lake plain area, which requires site specific conditions pertaining to site preparation, pit installation, well construction, and natural gas control.";
    meccaPoolText = "The proposed wellhead is located within the Mecca Pool where naturally occurring oil in the Berea Sandstone overlies the freshwater bearing Cussewago Sandstone, which is the deepest source of drinking water.";
    shallowText = "The proposed wellhead location is in an area where shallow (less than 300 feet below surface) gas has been encountered.";
    thinTillText = "The proposed wellhead location is in an area with thin glacial till over fractured bedrock aquifers that are susceptible to contamination from the surface, if not properly protected.";
    abandonedMineText = "The proposed wellhead location is in proximity of a potential mine void. According to Ohio Adm.Code 1501:9-1-08(M)(2) a mine string shall be set in order to isolate any such voids. However, this string may not serve as the sole water protection casing. Based on the location a mine string must be included in your proposed casing plan. However, if no void is encountered while drilling, the mine string may be waived.";
    activeMineText = "Your proposed location lies within the limits of an active mining operation. Conductor should be planned assuming water will be encountered before penetrating the working of any active underground mine.(OAC 1501:9-1-08 (M)(3)(a)(iii)) A mine string is required in an active underground mine please refer to ORC 1509.18, OAC 1501:9-1-08(M)(2))";
    municipalH20Text = "The proposed wellhead location is within the \"5-year time of travel boundary\" of a municipal water supply and will require site specific conditions regarding site preparation, pit construction, and well construction.";
    usdwText = "The proposed wellhead location is in an area where the protected USDW is the [[LOC]].  According to Ohio Adm. Code 1501:9-1-08(M)(4)(a) surface casing shall be set and cemented at least fifty (50) feet below the base of the deepest USDW before drilling through hydrocarbon bearing zones or areas which contain concentrations of total dissolved solids exceeding ten thousand (10,000) milligrams per liter";
    seismicText = "The proposed wellhead is located within an area of seismic activity with events greater than 2.0 magnitude that have occurred since 1999 or is located near a fault line.";
    urbanText = "The proposed wellhead is located within the limits of the [[LOC]] [[LOCNAME]] as defined by R.C. 1509.01(Y)";
    floodText = "The proposed location is within the defined 100-year flood plain as defined by the Federal Emergency Management Agnecy.  No earthen pits will be allowed when drilling in this area.  The extent of this area may be viewed on our interactive map.";
    bodiesOfH20Text = "The proposed wellhead location is within fifty (50) feet of a body of water or watercourse.  R.C.1509.021(L) prohibits the location of a new well or tank battery to be within fifty (50) feet of such a feature.  Upon review, this application may be rejected if the body of water or watercourse is found to be less than fifty (50) feet from the proposed location of the wellhead.";
    perennialStreamsText = "Your proposed wellhead location is within 500' of a perennial stream, and will require site specific conditions regarding site preparation, pit construction, and well construction.";
    stateLandsText = "The proposed wellhead location is located within state lands.";
    springsText = "Your proposed wellhead location is within 500' of a spring, and will require site specific conditions regarding site preparation, pit construction, and well construction.";
    waterWellProximityText = "Your proposed wellhead location is within 500' of a water well, and will require site specific conditions regarding site preparation, pit construction, and well construction.";
    wetlandsText = "The proposed wellhead location is within three hundred (300) feet of a wetland.  R.C.6111.021(B) requires that a permit be issued by the Environmental Protection Agency if a person proposes to engage in an activity that involves the filling of an isolated wetland.";
    roadsRailroadsText = "The proposed wellhead location is within fifty (50) feet of a road or railroad track.  R.C.1509.021(M) prohibits the location of a new well or tank battery to be within fifty (50) feet of such a feature.  Upon review, this application may be rejected if the road/railroad is found to be less than fifty (50) feet from the proposed location of the wellhead.";
   
    context: any;

    xCoordinate: any = null;
    yCoordinate: any = null;
    statePlane: any = null;
    polyline: number[][];
    wellType: WellDirection;
    dataSubscriptions: Subscription = new Subscription();

    public _queryResultCache: CacheService = new MemoryCache('gisQueries');
    _cacheTimeout: number = 600000;

    constructor(readonly contextService: ContextInteractionService, public readonly locationService: GisLocationService, public readonly queryService: GisQueryService, public readonly permitService: PermitService, readonly loader: LoaderService) {
    }

    ngOnDestroy() {
        if (this.dataSubscriptions) {
            this.dataSubscriptions.unsubscribe();
        }
    }

    generateCacheKey(latLong: number[], queryType): string {
        let key = "";

        if (latLong.length = 2) {
            key = `${latLong[0]},${latLong[1]},${queryType}`;
        }
        return key;
    }

    addToCacheIfNecessary(cacheKey: string, value: any, timeout: number = this._cacheTimeout) {
        if (!this._queryResultCache.has(cacheKey))
            this._queryResultCache.set(cacheKey, value, timeout);
    }

    getQueryPromise(latLong: number[], cacheKeyPromiseType: string, queryToExecute: () => any ): { promise: Promise<any>, cacheKey: string } {

        let promise: Promise<any>;
        let cacheKey = this.generateCacheKey(latLong, cacheKeyPromiseType);
        if (this._queryResultCache.has(cacheKey)) {
            promise = Promise.resolve(this._queryResultCache.get(cacheKey));
        }
        else
            promise = queryToExecute();

        return { promise, cacheKey};
    }

    clear(options: GISOptions): void {
        options.writeContextOptions.forEach(x => {
            let loc = this.getWriteContextLocation(x.index, options);
            if (loc)
                this.contextService.setContextData(loc, '', this.context);
        });
    }

    doQuery(options: GISOptions): boolean {
        let runQuery = false;
        if (this.xCoordinate && this.yCoordinate && this.statePlane) {
            for (let o = 0; o < options.writeContextOptions.length; o++) {
                const location = options.writeContextOptions[o].location;
                if (location && location !== "") {
                    const result = this.contextService.getContextData(options.writeContextOptions[o].location, this.context);
                    runQuery = !result || !result.value;
                    if (!runQuery) break;
                }
            }
        }
        return runQuery;
    }

    doReset(): boolean {
        if (this.xCoordinate && this.yCoordinate && this.statePlane) return false;
        return true;
    }
    

    hasOptions(options: GISOptions): boolean {
        if ((!options.readContextOptions && !options.alternateReadContextOptions) || !options.writeContextOptions) return false;
        return true;
    }

    useOptionSet(optionSet: WellConfigurationData[], wellDirection: WellDirection = null): boolean {
        if (wellDirection) {
            const opts = [];
            let xOpt;
            let yOpt;
            let pOpt;
            switch (wellDirection) {
                case WellDirection.Vertical:
                    xOpt = optionSet.find(o => o.index === WellConfigurationIndex.LongitudeVertical);
                    yOpt = optionSet.find(o => o.index === WellConfigurationIndex.LatitudeVertical);
                    pOpt = optionSet.find(o => o.index === WellConfigurationIndex.StatePlaneVertical);
                    break;
                case WellDirection.Directional:
                    xOpt = optionSet.find(o => o.index === WellConfigurationIndex.LongitudeDirectional);
                    yOpt = optionSet.find(o => o.index === WellConfigurationIndex.LatitudeDirectional);
                    pOpt = optionSet.find(o => o.index === WellConfigurationIndex.StatePlaneDirectional);
                    break;
                case WellDirection.Horizontal:
                case WellDirection.OtherHorizontal:
                    xOpt = optionSet.find(o => o.index === WellConfigurationIndex.LongitudeHorizontal);
                    yOpt = optionSet.find(o => o.index === WellConfigurationIndex.LatitudeHorizontal);
                    pOpt = optionSet.find(o => o.index === WellConfigurationIndex.StatePlaneHorizontal);
                    break;
            }
            opts.push(xOpt);
            opts.push(yOpt);
            opts.push(pOpt);
            return opts.every((value) => { return (isDefined(value) && isDefined(value.location) && value.location !== "");  });        
        } else {
            return optionSet.every((value) => { return (isDefined(value.location) && value.location !== ""); });       
        }
             
    }    

    queryCoordinates(options: GISOptions, config: GISConfig): Promise<boolean> {
        this.polyline = null;
        let obs: Promise<boolean>;
        let latLong: number[];
        if (!this.hasOptions)
            return;
        const directionLoc = this.getReadContextLocation(WellConfigurationIndex.WellDirection, options.readContextOptions);
        let wellDirection: WellDirection;
        if (directionLoc) {
            wellDirection = this.contextService.getContextData(directionLoc, this.context);
        }
        if (this.useOptionSet(options.readContextOptions, wellDirection)) {            
            switch (wellDirection) {
                case WellDirection.Vertical:
                    this.wellType = WellDirection.Vertical;
                    this.xCoordinate = this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.LongitudeVertical, options.readContextOptions), this.context);
                    this.yCoordinate = this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.LatitudeVertical, options.readContextOptions), this.context);
                    this.statePlane = this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.StatePlaneVertical, options.readContextOptions), this.context);
                    break;
                case WellDirection.OtherHorizontal:
                case WellDirection.Horizontal:
                    this.wellType = WellDirection.Horizontal;
                    this.xCoordinate = this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.LongitudeHorizontal, options.readContextOptions), this.context);
                    this.yCoordinate = this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.LatitudeHorizontal, options.readContextOptions), this.context);
                    this.statePlane = this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.StatePlaneHorizontal, options.readContextOptions), this.context);
                    break;
                case WellDirection.Directional:
                    this.wellType = WellDirection.Directional;
                    this.xCoordinate = this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.LongitudeDirectional, options.readContextOptions), this.context);
                    this.yCoordinate = this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.LatitudeDirectional, options.readContextOptions), this.context);
                    this.statePlane = this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.StatePlaneDirectional, options.readContextOptions), this.context);
                    break;
            }
            const plane = (this.statePlane.toLowerCase() === StatePlane.South)
                ? CoordinateSystem.OHIO_SOUTH
                : CoordinateSystem.OHIO_NORTH;

            if (this.xCoordinate)
                this.xCoordinate = Number(this.xCoordinate);
            else
                this.xCoordinate = NaN;
            if (this.yCoordinate)
                this.yCoordinate = Number(this.yCoordinate);
            else
                this.yCoordinate = NaN;


            if (this.wellType !== WellDirection.Vertical) {
                let points: number[][] = [];

                if (!isNaN(this.xCoordinate) && !isNaN(this.yCoordinate) && plane)
                    points.push(this.locationService.statePlaneToWebMercator(this.xCoordinate, this.yCoordinate, plane).reverse());

                const targetX = Number(this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.TargetNADX, options.readContextOptions), this.context));
                const targetY = Number(this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.TargetNADY, options.readContextOptions), this.context));
                let targetStatePlane = this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.TargetStatePlane, options.readContextOptions), this.context);

                if (this.wellType !== WellDirection.Directional) {
                    const firstTakeX = Number(this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.FirstTakeNADX, options.readContextOptions), this.context));
                    const firstTakeY = Number(this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.FirstTakeNADY, options.readContextOptions), this.context));
                    let firstTakeStatePlane = this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.FirstTakeStatePlane, options.readContextOptions), this.context);
                    const lastTakeX = Number(this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.LastTakeNADX, options.readContextOptions), this.context));
                    const lastTakeY = Number(this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.LastTakeNADY, options.readContextOptions), this.context));
                    let lastTakeStatePlane = this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.LastTakeStatePlane, options.readContextOptions), this.context);

                    if (!isNaN(firstTakeX) && !isNaN(firstTakeY) && firstTakeStatePlane) {
                        firstTakeStatePlane = (firstTakeStatePlane.toLowerCase() === StatePlane.South)
                            ? CoordinateSystem.OHIO_SOUTH
                            : CoordinateSystem.OHIO_NORTH;

                        points.push(this.locationService.statePlaneToWebMercator(firstTakeX, firstTakeY, firstTakeStatePlane).reverse());
                    }
                    if (!isNaN(lastTakeX) && !isNaN(lastTakeY) && lastTakeStatePlane) {
                        lastTakeStatePlane = (lastTakeStatePlane.toLowerCase() === StatePlane.South)
                            ? CoordinateSystem.OHIO_SOUTH
                            : CoordinateSystem.OHIO_NORTH;

                        points.push(this.locationService.statePlaneToWebMercator(lastTakeX, lastTakeY, lastTakeStatePlane).reverse());
                    }
                }

                if (!isNaN(targetX) && !isNaN(targetY) && targetStatePlane) {
                    targetStatePlane = (targetStatePlane.toLowerCase() === StatePlane.South)
                        ? CoordinateSystem.OHIO_SOUTH
                        : CoordinateSystem.OHIO_NORTH;

                    points.push(this.locationService.statePlaneToWebMercator(targetX, targetY, targetStatePlane).reverse());
                }

                if (points.length > 1)
                    this.polyline = points;
            }
            if (!isNaN(this.xCoordinate) && !isNaN(this.yCoordinate) && this.statePlane !== "") {
                latLong = this.locationService.statePlaneToWebMercator(this.xCoordinate, this.yCoordinate, plane, true);
            } else {
                this.clear(options);
                obs = observableOf(false).toPromise();
            }

        } else if (this.useOptionSet(options.alternateReadContextOptions, wellDirection)) {
            let latitude = this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.LatitudeNumeric, options.alternateReadContextOptions), this.context);
            let longitude = this.contextService.getContextData(this.getReadContextLocation(WellConfigurationIndex.LongitudeNumeric, options.alternateReadContextOptions), this.context);
            if (isDefined(latitude) && latitude !== "" && isDefined(longitude) && longitude !== "") {
                latLong = [Number(latitude), Number(longitude)];
            }            
        } else {
            obs = observableOf(false).toPromise();
            
        }
        if (isDefined(latLong)) {
            const writeLatitudeLoc = this.getWriteContextLocation(WellConfigurationIndex.CalculatedLatitude, options);
            const writeLongitudeLoc = this.getWriteContextLocation(WellConfigurationIndex.CalculatedLongitude, options);
            if (writeLatitudeLoc && writeLatitudeLoc !== "") {
                this.contextService.setContextData(writeLatitudeLoc, latLong[0], this.context);
            }
            if (writeLongitudeLoc && writeLongitudeLoc !== "") {
                this.contextService.setContextData(writeLongitudeLoc, latLong[1], this.context);
            }
            const waterLatitude = this.getWriteContextLocation(WellConfigurationIndex.WaterSourceLatitude, options);
            if (waterLatitude && waterLatitude !== "") {
                this.contextService.setContextData(this.getWriteContextLocation(WellConfigurationIndex.WaterSourceLatitude, options), latLong[0], this.context);
            }
            const waterLongitude = this.getWriteContextLocation(WellConfigurationIndex.WaterSourceLongitude, options);
            if (waterLongitude && waterLongitude !== "") {
                this.contextService.setContextData(this.getWriteContextLocation(WellConfigurationIndex.WaterSourceLongitude, options), latLong[1], this.context);
            }
            const promiseList: Promise<any>[] = [];
            promiseList.push(this.promiseSectionLotTract(latLong, options, config));
            promiseList.push(this.promiseQuadrangle(latLong, options, config));
            promiseList.push(this.promiseCounty(latLong, options, config));
            promiseList.push(this.promiseTownship(latLong, options, config));
            const urban = this.getWriteContextLocation(WellConfigurationIndex.UrbanArea, options);
            if (urban) {
                promiseList.push(this.promiseUrbanization(latLong, urban));
            }
            const wellhead = this.getWriteContextLocation(WellConfigurationIndex.WellheadToWellheadVariance, options);
            if (wellhead) {
                promiseList.push(this.promiseWellheadVariance(latLong, wellhead, config));
            }
            const watershed = this.getWriteContextLocation(WellConfigurationIndex.Watershed, options);
            if (watershed) {
                promiseList.push(this.promiseWatershed(latLong, watershed, config));
            }
            const state = this.getWriteContextLocation(WellConfigurationIndex.State, options);
            if (state) {
                promiseList.push(this.promiseState(latLong, state));
            }
            const conditions = this.promiseSpecialPermitConditions(latLong, options, config);
            conditions.forEach(c => promiseList.push(c));
            obs = Promise.all(promiseList).then((results) => {
                console.log("promise chain complete");
                return true;
            }, (reject) => {
                console.error(reject);
                console.log("promise chain failed");
                return false;
            });
        } else {
            obs = observableOf(false).toPromise();
        }
        return obs;      
    }

    resetAllStoredValues(options: GISOptions): void {
        if (options && options.writeContextOptions) {
            for (let o = 0; o < options.writeContextOptions.length; o++) {
                this.contextService.setContextData(options.writeContextOptions[o].location, "", this.context);
            }
        }
    }

    promiseCounty(latLong: number[], options: GISOptions, config: GISConfig): Promise<any> {
        const countyNameLoc = this.getWriteContextLocation(WellConfigurationIndex.CountyName, options);
        const countyCodeLoc = this.getWriteContextLocation(WellConfigurationIndex.CountyCode, options);
        if (countyNameLoc || countyCodeLoc) {
            let promiseResponse = this.getQueryPromise(latLong, "promiseCounty", () => this.queryService.resolveQueryAtPoint(config.yearlyGISDataUrl, "Counties", latLong));

            return promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                let countyName = "";
                let countyCode = "";
                if (result && result.features && result.features.length > 0) {

                    countyName = result.features[0].attributes["COUNTY"] || "";
                    countyCode = result.features[0].attributes["FIPS"] || ""
                }
                if (countyNameLoc) {
                    this.contextService.setContextData(countyNameLoc, `${countyName}`, this.context);
                }
                if (countyCodeLoc) {
                    this.contextService.setContextData(countyCodeLoc, `${countyCode}`, this.context);
                }
                return { code: countyCode, name: countyName };

            }, (error) => {
                console.log(`Error is getting state: ${error}`);
                return { code: null, name: null };
            }).then((result) => {
                if (!result.code && !result.name) return;
                const inspectorNameLoc = this.getWriteContextLocation(WellConfigurationIndex.InspectorName, options);
                const inspectorPhoneLoc = this.getWriteContextLocation(WellConfigurationIndex.InspectorPhone, options);
                const backupInspNameLoc = this.getWriteContextLocation(WellConfigurationIndex.BackupInspectorName, options);
                const backupInspPhoneLoc = this.getWriteContextLocation(WellConfigurationIndex.BackupInspectorPhone, options);
                const regionalPhoneLoc = this.getWriteContextLocation(WellConfigurationIndex.RegionalPhone, options);
                const supervisorNameLoc = this.getWriteContextLocation(WellConfigurationIndex.SupervisorName, options);
                const supervisorPhoneLoc = this.getWriteContextLocation(WellConfigurationIndex.SupervisorPhone, options);
                if (inspectorNameLoc || inspectorPhoneLoc || backupInspNameLoc || backupInspPhoneLoc || regionalPhoneLoc || supervisorNameLoc || supervisorPhoneLoc) {
                    let promiseResponse = this.getQueryPromise(latLong, "promiseCountyInspector",
                        () => Promise.resolve(this.permitService.searchInspectorList({ countyCode: result.code, countyName: result.name })));

                    promiseResponse.promise.then((inspectorObservable) => {
                        this.dataSubscriptions.add(inspectorObservable.pipe(first()).subscribe((insp) => {
                            this.addToCacheIfNecessary(promiseResponse.cacheKey, of(insp));

                            if (insp && insp.length > 0) {
                                if (inspectorNameLoc) {
                                    this.contextService.setContextData(inspectorNameLoc, insp[0].inspectorName, this.context);
                                }
                                if (inspectorPhoneLoc) {
                                    this.contextService.setContextData(inspectorPhoneLoc, insp[0].inspectorPhone, this.context);
                                }
                                if (backupInspNameLoc) {
                                    this.contextService.setContextData(backupInspNameLoc, insp[0].backupInspectorName, this.context);
                                }
                                if (backupInspPhoneLoc) {
                                    this.contextService.setContextData(backupInspPhoneLoc, insp[0].backupInspectorPhone, this.context);
                                }
                                if (regionalPhoneLoc) {
                                    this.contextService.setContextData(regionalPhoneLoc, insp[0].regionalPhone, this.context);
                                }
                                if (supervisorNameLoc) {
                                    this.contextService.setContextData(supervisorNameLoc, insp[0].supervisorName, this.context);
                                }
                                if (supervisorPhoneLoc) {
                                    this.contextService.setContextData(supervisorPhoneLoc, insp[0].supervisorPhone, this.context);
                                }
                            }
                        }, (error) => {
                            console.log(`Error getting inspector information: ${error}`);
                        }));
                    }, (error) => {
                    console.log(`Error getting inspector information: ${error}`);
                    });
                }
            });
        } else {
            return observableOf("").toPromise();
        }

    }

    promiseQuadrangle(latLong: number[], options, config: GISConfig): Promise<any> {
        const quadrangleNameLoc = this.getWriteContextLocation(WellConfigurationIndex.QuadrangleName, options);
        const quadrangleCodeLoc = this.getWriteContextLocation(WellConfigurationIndex.QuadrangleCode, options);
        if (quadrangleNameLoc || quadrangleCodeLoc) {
            let promiseResponse = this.getQueryPromise(latLong, "promiseQuadrangle",
                () => this.queryService.resolveQueryAtPoint(config.yearlyGISDataUrl, "quad24k", latLong));

            return promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                let quadrangleName = "No quadrangle data available";
                let quadrangleNameCode = "";
                if (result && result.features && result.features.length > 0) {
                    quadrangleName = result.features[0].attributes["QUADNAME"] || "";
                    quadrangleNameCode = result.features[0].attributes["OGSCODE"] || "";
                }
                if (quadrangleNameLoc) {
                    this.contextService.setContextData(quadrangleNameLoc, quadrangleName, this.context);
                }
                if (quadrangleCodeLoc) {
                    this.contextService.setContextData(quadrangleCodeLoc, quadrangleNameCode, this.context);
                }
            },
                (error) => {
                    console.log(`Error in getting quadrangle: ${error}`);
                });
        }
        return observableOf("").toPromise();

    }

    promiseSectionLotTract(latLong: number[], options, config: GISConfig): Promise<any> {
        const sectionLotTractLoc = this.getWriteContextLocation(WellConfigurationIndex.SectionLotTract, options);
        const sectionLoc = this.getWriteContextLocation(WellConfigurationIndex.Section, options);
        const lotLoc = this.getWriteContextLocation(WellConfigurationIndex.Lot, options);
        const tractLoc = this.getWriteContextLocation(WellConfigurationIndex.Tract, options);
        const townshipLoc = this.getWriteContextLocation(WellConfigurationIndex.TownshipNumber, options);

        if (sectionLotTractLoc || sectionLoc || lotLoc || tractLoc || townshipLoc) {
            let promiseResponse = this.getQueryPromise(latLong, "promiseSectionLotTract",
                () => this.queryService.resolveQueryAtPoint(config.yearlyGISDataUrl, "land_subdivisions_old", latLong));

            return promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                if (result && result.features && result.features.length > 0) {
                    const data = result.features[0].attributes;
                    let section = "";
                    let lot = "";
                    let tract = "";
                    if (isDefined(data["SECTION_"])) {
                        section = (data["SECTION_"].toString().trim().length !== 0)
                            ? data["SECTION_"]
                            : "";
                    }
                    if (isDefined(data["LOT"])) {
                        lot = (data["LOT"].toString().trim().length !== 0)
                            ? data["LOT"]
                            : "";
                    }
                    if (isDefined(result["TRACT"])) {
                        tract = (data["TRACT"].toString().trim().length !== 0)
                            ? data["TRACT"]
                            : "";
                    }
                    if (sectionLotTractLoc) {
                        this.contextService.setContextData(sectionLotTractLoc, `${section}/${lot}/${tract}`, this.context);
                    }
                    if (sectionLoc) {
                        this.contextService.setContextData(sectionLoc, section, this.context);
                    }
                    if (lotLoc) {
                        this.contextService.setContextData(lotLoc, lot, this.context);
                    }
                    if (tractLoc) {
                        this.contextService.setContextData(tractLoc, tract, this.context);
                    }

                    if (townshipLoc) {
                        let townshipNumber = "";
                        if (isDefined(data["CVLTWP_NO"])) {
                            townshipNumber = (data["CVLTWP_NO"].toString().trim().length !== 0) ? data["CVLTWP_NO"] : "";
                        }
                        this.contextService.setContextData(townshipLoc, townshipNumber, this.context);
                    }
                }
            }, (error) => {
                console.log(`Error in getting section-lot-tract: ${error}`);
            });
        } else {
            return observableOf("").toPromise();       
        }         
    }

    promiseSpecialPermitConditions(latLong: number[], options: GISOptions, config: GISConfig): Promise<any>[] {
        const yearlyUrl = config.yearlyGISDataUrl;
        const biweeklyUrl = config.biWeeklyGISDataUrl;
        const dailyUrl = config.dailyGISDataUrl;
        const layers = [];

        //Bodies of Water
        const bodiesH20 = this.getWriteContextLocation(WellConfigurationIndex.ConditionBodiesOfWater, options);
        if (bodiesH20 && bodiesH20 !== "") {
            let promiseResponse = this.getQueryPromise(latLong, "bodiesH20Promise", () => this.queryService.resolveBufferQueryAtPoint(yearlyUrl, "Springs", latLong, 50));

            const bodiesH20Promise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                let data = "";
                if (result && result.features && result.features.length > 0 && result.features[0].attributes !== "") {
                    data = this.bodiesOfH20Text;
                }
                this.contextService.setContextData(bodiesH20, data, this.context);
            }, (error) => { console.log(error); });
            layers.push(bodiesH20Promise);
        }
        //Drift Thickness
        const drift = this.getWriteContextLocation(WellConfigurationIndex.ConditionDriftThickness, options);
        if (drift && drift !== "") {
            let promiseResponse = this.getQueryPromise(latLong, "driftPromise", () => this.queryService.queryImageServerAtPoint(config.glacialDriftGISUrl, latLong));

            const driftPromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                if (result && result.value) {
                    let data = "";
                    if (result.value !== "NoData") {
                        data = this.driftThicknessText.replace("[[THICKNESS]]", result.value);
                    }
                    this.contextService.setContextData(drift, data, this.context);
                }
            }, (error) => { console.log(error); });
            layers.push(driftPromise);
        }
        //Flood Plain
        const flood = this.getWriteContextLocation(WellConfigurationIndex.ConditionHundredYearFloodPlain, options);
        if (flood && flood !== "") {
            let promiseResponse = this.getQueryPromise(latLong, "floodPromise", () => this.queryService.resolveQueryAtPoint(yearlyUrl, "S_Fld_Haz_Ar", latLong));

            const floodPromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                let zoneText = "";
                if (result && result.features && result.features.length > 0) {
                    const zoneAttribute = result.features[0].attributes["FLD_ZONE"];
                    const qualifyingZonePrefix = ["A", "V"]; //Source: https://www.fema.gov/flood-zones
                    const zone = zoneAttribute.toString().substring(0, 1);
                    if (qualifyingZonePrefix.indexOf(zone) !== -1) {
                        zoneText = this.floodText;
                    }
                }
                this.contextService.setContextData(flood, zoneText, this.context);

            }, (error) => { console.log(error); });
            layers.push(floodPromise);
        }
        //Perennial Streams
        const perennialStreams = this.getWriteContextLocation(WellConfigurationIndex.ConditionPerennialStreams, options);
        if (perennialStreams && perennialStreams !== "") {
            this.contextService.setContextData(perennialStreams, "", this.context); //ensure there's a blank value in case no promises resolve with a result
            let promiseResponse = this.getQueryPromise(latLong, "ndhFlowlinePromise", () => this.queryService.resolveBufferQueryAtPoint(yearlyUrl, "NHDFlowline", latLong, 500));

            const ndhFlowlinePromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                if (result && result.features && result.features.length > 0) {
                    this.contextService.setContextData(perennialStreams, this.perennialStreamsText, this.context);
                }
            }, (error) => { console.log(error); });

            layers.push(ndhFlowlinePromise);
        }
        //Springs
        const springs = this.getWriteContextLocation(WellConfigurationIndex.ConditionSprings, options);
        if (springs && springs !== "") {
            let promiseResponse = this.getQueryPromise(latLong, "springsPromise", () => this.queryService.resolveBufferQueryAtPoint(yearlyUrl, "Springs", latLong, 500));

            const springsPromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                let data = "";
                if (result && result.features && result.features.length > 0) {
                    data = this.springsText;
                }
                this.contextService.setContextData(springs, data, this.context);
            }, (error) => { console.log(error); });

            layers.push(springsPromise);
        }
        //Hydrogen Sulfide   
        const h2s = this.getWriteContextLocation(WellConfigurationIndex.ConditionHydrogenSulfide, options);
        if (h2s && h2s !== "") {
            let promiseResponse = this.getQueryPromise(latLong, "h2sPromise", () => this.queryService.resolveQueryAtPoint(yearlyUrl, "H2S_Townships", latLong));

            const h2sPromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                let hydSulfText = "";
                if (result && result.features && result.features.length > 0) {
                    const hasH2s = result.features[0].attributes["H2S"] !== null;
                    if (hasH2s) {
                        hydSulfText = this.h2sText;
                    }
                }
                this.contextService.setContextData(h2s, hydSulfText, this.context);
            }, (error) => { console.log(error); });
            layers.push(h2sPromise);
        }
        //Injection Well AOR
        const wellAOR = this.getWriteContextLocation(WellConfigurationIndex.ConditionInjectionWellAOR, options);
        if (wellAOR && wellAOR !== "") {
            let promiseResponse = this.getQueryPromise(latLong, "aorPromise", () => this.queryService.resolveQueryAtPoint(dailyUrl, "InjectionWell_AOR_halfmile", latLong));

            const aorPromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                let data = "";
                if (result && result.features.length > 0) {
                    data = this.aorText;
                }
                this.contextService.setContextData(wellAOR, data, this.context);
            }, (error) => { console.log(error); });
           layers.push(aorPromise);
        }
        //Lake Plain
        //Mecca Pool
        const lake = this.getWriteContextLocation(WellConfigurationIndex.ConditionLakePlain, options);
        const mecca = this.getWriteContextLocation(WellConfigurationIndex.ConditionMeccaPool, options);
        if ((lake && lake !== "") || (mecca && mecca !== "")) {
            let promiseResponse = this.getQueryPromise(latLong, "meccaOrLakePromise", () => this.queryService.resolveQueryAtPoint(yearlyUrl, "Permit_Condition_Areas", latLong));

            const meccaOrLakePromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                let meccaString = "";
                let lakeString = "";
                if (result && result.features.length > 0) {
                    for (let f = 0; f < result.features.length; f++) {
                        const name = result.features[f].attributes["Name"].toString();
                        switch (name) {
                            case "Mecca Pool":
                                meccaString = this.meccaPoolText;
                                break;
                            case "Lake Plain Region":
                                lakeString = this.lakePlainText;
                                break;
                        }
                    }
                }
                if (lake && lake !== "") {
                    this.contextService.setContextData(lake, lakeString, this.context);
                }
                if (mecca && mecca !== "") {
                    this.contextService.setContextData(mecca, meccaString, this.context);
                }
            }, (error) => { console.log(error); });
            layers.push(meccaOrLakePromise);
        }
        //Abandoned Mines
        const abandoned = this.getWriteContextLocation(WellConfigurationIndex.ConditionAbandonedMines, options);
        if (abandoned && abandoned !== "") {
            this.contextService.setContextData(abandoned, "", this.context); //ensure there's a blank value in case neither promise resolves with a result

            let promiseResponse = this.getQueryPromise(latLong, "abandonedPromise1", () => this.queryService.resolveQueryAtPoint(biweeklyUrl, "AUM_MINES", latLong));
            let promiseResponse2 = this.getQueryPromise(latLong, "abandonedPromise2", () => this.queryService.resolveQueryAtPoint(biweeklyUrl, "AUM_UNKNOWN_EXTENT", latLong));

            const abandonedPromise1 = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                if (result && result.features && result.features.length > 0) {
                    this.contextService.setContextData(abandoned, this.abandonedMineText, this.context);
                }
            }, (error) => { console.log(`Error in abandoned mines promise 1: ${error}`); });
            const abandonedPromise2 = promiseResponse2.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse2.cacheKey, result);

                if (result && result.features && result.features.length > 0) {
                    this.contextService.setContextData(abandoned, this.abandonedMineText, this.context);
                }
            }, (error) => { console.log(`Error in abandoned mines promise 2: ${error}`); });
            layers.push(abandonedPromise1);
            layers.push(abandonedPromise2);
        }
        //Active Mines
        const active = this.getWriteContextLocation(WellConfigurationIndex.ConditionActiveMines, options);
        if (active && active !== "") {
            this.contextService.setContextData(active, "", this.context); //ensure there's a blank value in case neither promise resolves with a result

            let promiseResponse = this.getQueryPromise(latLong, "activePromise1",
                () => this.queryService.resolveQueryAtPoint(biweeklyUrl, "UG_IM_Ext", latLong));
            let promiseResponse2 = this.getQueryPromise(latLong, "activePromise2",
                () => this.queryService.resolveQueryAtPoint(biweeklyUrl, "UG_Mine_Ext", latLong));

            const activePromise1 = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                if (result && result.features && result.features.length > 0) {
                    this.contextService.setContextData(active, this.activeMineText, this.context);
                }
            }, (error) => { console.log(`Error in active mines promise 1: ${error}`); });
            const activePromise2 = promiseResponse2.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse2.cacheKey, result);

                if (result && result.features && result.features.length > 0) {
                    this.contextService.setContextData(active, this.activeMineText, this.context);
                }
            }, (error) => { console.log(`Error in active mines promise 2: ${error}`); });
            layers.push(activePromise1);
            layers.push(activePromise2);
        }
        //Water Wells proximity alert
        const waterWellProximity = this.getWriteContextLocation(WellConfigurationIndex.ConditionWaterWellProximity, options);
        if (waterWellProximity && waterWellProximity !== "") {
            let promiseResponse = this.getQueryPromise(latLong, "waterWellProximityPromise",
                () => this.queryService.resolveBufferQueryAtPoint(dailyUrl, "WaterWells", latLong, 500));

            const waterWellProximityPromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                let data = "";
                if (result && result.features && result.features.length > 0 && result.features[0].attributes !== "") {
                    data = this.waterWellProximityText;
                }
                this.contextService.setContextData(waterWellProximity, data, this.context);
            }, (error) => { console.log(error); });
            layers.push(waterWellProximityPromise);
        }

        //Municipal Water Supply
        const municipalH20 = this.getWriteContextLocation(WellConfigurationIndex.ConditionMunicipalWater, options);
        if (municipalH20 && municipalH20 !== "") {
            this.contextService.setContextData(municipalH20, "", this.context); //ensure there's a blank value in case neither promise resolves with a result

            let promiseResponse = this.getQueryPromise(latLong, "surfaceWaterProtection5YearBoundaryPromise",
                () => this.queryService.resolveQueryAtPoint(config.fiveYearTravelDataUrl, "SURFACE_WATER_PROTECTION_AREA_5_YEAR_TRAVEL_TIME", latLong));

            let promiseResponse2 = this.getQueryPromise(latLong, "inlandSurfaceWaterPromise",
                () => this.queryService.resolveQueryAtPoint(config.inlandSurfaceWaterSWPAUrl, "Emergency_Management_Zones", latLong));

            const surfaceWaterProtection5YearBoundaryPromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                if (result && result.features && result.features.length > 0) {
                    this.contextService.setContextData(municipalH20, this.municipalH20Text, this.context);
                }
            }, (error) => { console.log(error); });
            const inlandSurfaceWaterPromise = promiseResponse2.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse2.cacheKey, result);

                if (result && result.features && result.features.length > 0) {
                    this.contextService.setContextData(municipalH20, this.municipalH20Text, this.context);
                }
            }, (error) => { console.log(error); });

            layers.push(inlandSurfaceWaterPromise);

            layers.push(surfaceWaterProtection5YearBoundaryPromise);
        }

        //Roads / Railroads
        const roads = this.getWriteContextLocation(WellConfigurationIndex.ConditionRoadsRailroads, options);
        if (roads && roads !== "") {
            this.contextService.setContextData(roads, "", this.context); //ensure there's a blank value in case neither promise resolves with a result

            let promiseResponse = this.getQueryPromise(latLong, "activeRailPromise", () => this.queryService.resolveBufferQueryAtPoint(yearlyUrl, "Active_Raillines", latLong, 50));
            let promiseResponse2 = this.getQueryPromise(latLong, "abandonedRailPromise", () => this.queryService.queryBufferAtPoint(yearlyUrl, "Abandoned_Raillines", latLong, 50));
            let promiseResponse3 = this.getQueryPromise(latLong, "roadsPromise", () => this.queryService.resolveBufferQueryAtPoint(yearlyUrl, "LBRS", latLong, 50));

            const activeRailPromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                if (result && result.features && result.features.length > 0 && result.features[0].attributes !== "") {
                    this.contextService.setContextData(roads, this.roadsRailroadsText, this.context);
                }
            }, (error) => { console.log(error); });
            const abandonedRailPromise = promiseResponse2.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse2.cacheKey, result);

                if (result && result !== "") {
                    this.contextService.setContextData(roads, this.roadsRailroadsText, this.context);
                }
            }, (error) => { console.log(error); });
            const roadsPromise = promiseResponse3.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse3.cacheKey, result);

                if (result && result.features && result.features.length > 0 && result.features[0].attributes !== "") {
                    this.contextService.setContextData(roads, this.roadsRailroadsText, this.context);
                }
            }, (error) => { console.log(error); });
            layers.push(activeRailPromise);
            layers.push(abandonedRailPromise);
            layers.push(roadsPromise);
        }
        //Seismic Area
        const seismic = this.getWriteContextLocation(WellConfigurationIndex.ConditionSeismicArea, options);
        if (seismic && seismic !== "") {
            this.contextService.setContextData(seismic, "", this.context);

            let promiseResponse = this.getQueryPromise(latLong, "ellipsePromise", () => this.polyline ? this.queryService.queryBufferPolyline(biweeklyUrl, "OHNet_Ellipse_Polys", this.polyline, 100)
                                                                                                      : this.queryService.resolveQueryAtPoint(biweeklyUrl, "OHNet_Ellipse_Polys", latLong));
            let promiseResponse2 = this.getQueryPromise(latLong, "epicenterPromise", () => this.polyline ? this.queryService.queryBufferPolyline(biweeklyUrl, "RECENT_EQ_EPICENTER_3MI_BUFFER", this.polyline, 100)
                                                                                                         : this.queryService.resolveQueryAtPoint(biweeklyUrl, "RECENT_EQ_EPICENTER_3MI_BUFFER", latLong));
            let promiseResponse3 = this.getQueryPromise(latLong, "faultLinesPromise", () => this.polyline ? this.queryService.queryBufferPolyline(yearlyUrl, "Known_Precambrian_Faults_3mi_buffer", this.polyline, 100)
                                                                                                     : this.queryService.resolveQueryAtPoint(yearlyUrl, "Known_Precambrian_Faults_3mi_buffer", latLong));

            const ellipsePromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                if (result && result.features && result.features.length > 0) {
                    this.contextService.setContextData(seismic, this.seismicText, this.context);
                }
            }, (error) => { console.log(error); });
            const epicenterPromise = promiseResponse2.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse2.cacheKey, result);

                if (result && result.features && result.features.length > 0) {
                    this.contextService.setContextData(seismic, this.seismicText, this.context);
                }
            }, (error) => { console.log(error); });
            const faultLinesPromise = promiseResponse3.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse3.cacheKey, result);

                if (result && result.features && result.features.length > 0) {
                    this.contextService.setContextData(seismic, this.seismicText, this.context);
                }
            }, (error) => { console.log(error); });

            layers.push(ellipsePromise);
            layers.push(epicenterPromise);
            layers.push(faultLinesPromise);
        }
        //Shallow Gas
        const gas = this.getWriteContextLocation(WellConfigurationIndex.ConditionShallowSurface, options);
        if (gas && gas !== "") {
            this.contextService.setContextData(gas, "", this.context);
            let promiseResponse = this.getQueryPromise(latLong, "gasPromise", () => this.queryService.resolveQueryAtPoint(yearlyUrl, "ShallowSurfaceCasing_Townships", latLong));
            const gasPromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                if (result && result.features.length > 0) {
                    this.contextService.setContextData(gas, this.shallowText, this.context);
                }
            }, (error) => { console.log(error); });
            layers.push(gasPromise);
        }
        //Thin Till
        const till = this.getWriteContextLocation(WellConfigurationIndex.ConditionThinTill, options);
        if (till && till !== "") {
            this.contextService.setContextData(till, "", this.context);
            let promiseResponse = this.getQueryPromise(latLong, "tillPromise", () => this.queryService.resolveQueryAtPoint(yearlyUrl, "Thin_Till_poly", latLong));
            const tillPromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                if (result && result.features && result.features.length > 0) {
                    this.contextService.setContextData(till, this.thinTillText, this.context);
                }
            }, (error) => { console.log(error); });
            layers.push(tillPromise);
        }
        //Underground Source of Drinking Water (USDW)
        const usdw = this.getWriteContextLocation(WellConfigurationIndex.ConditionUndergroundDrinkingWater, options);
        if (usdw && usdw !== "") {
            this.contextService.setContextData(usdw, "", this.context);
            let promiseResponse = this.getQueryPromise(latLong, "usdwBoundaryPromise", () => this.queryService.resolveQueryAtPoint(config.usdwAreasUrl, "USDW__Areas", latLong));
            const usdwBoundaryPromise = promiseResponse.promise.then((result) => {
                let msg = "";
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);
                if (result && result.features && result.features.length > 0) {
                    const area = result.features[0].attributes["USDWAREAS"];
                    if (area.toLowerCase() !== "not mapped") {
                        msg = this.usdwText.replace("[[LOC]]", area);
                    }
                }
                this.contextService.setContextData(usdw, msg, this.context);
            }, (error) => { console.log(error); });
            layers.push(usdwBoundaryPromise);
        }
        //Urban Area
        const urban = this.getWriteContextLocation(WellConfigurationIndex.ConditionUrbanArea, options);
        if (urban && urban !== "") {
            let promiseResponse = this.getQueryPromise(latLong, "urbanPromise", () => this.queryService.resolveQueryAtPoint(yearlyUrl, "Urbanized_Areas", latLong));
            const urbanPromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);
                if (result) {
                    let data = "";
                    if (result.features.length > 0) {
						let population: number = result.features[0].attributes["POPULATION"];
						if (population == null) {
							population = result.features[0].attributes["POP_2020"];
						}
						const popResult = this.setUrbanization(population, result.features[0].attributes["TYPE"]);

                        if (popResult !== "NONURB") {
                            data = this.urbanText.replace("[[LOC]]", result.features[0].attributes["TYPE"].toString().toLowerCase());
                            const location = result.features[0].attributes["NAME"].toString();
                            const locationParts = location.split(" ");
                            let name = "";
                            for (let i = 0; i < locationParts.length; i++) {
                                const ending = locationParts[i].substring(1, locationParts[i].length).toLowerCase();
                                const initial = locationParts[i].substring(0, 1).toUpperCase();
                                locationParts[i] = `${initial}${ending}`;
                                name = (name.length === 0) ? locationParts[i] : `${name} ${locationParts[i]}`;
                            }
                            data = data.replace("[[LOCNAME]]", name);
                        }
                    }
                    this.contextService.setContextData(urban, data, this.context);
                }
            });
            layers.push(urbanPromise);
        }
        //Wetlands
        const wetlands = this.getWriteContextLocation(WellConfigurationIndex.ConditionWetlands, options);
        if (wetlands && wetlands !== "") {
            let promiseResponse = this.getQueryPromise(latLong, "wetlandsPromise", () => this.queryService.resolveBufferQueryAtPoint(yearlyUrl, "Wetlands", latLong, 300));
            const wetlandsPromise = promiseResponse.promise.then((result) => {
                let data = "";
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);
                if (result && result.features && result.features.length > 0 && result.features[0].attributes !== "") {
                    data = this.wetlandsText;
                }
                this.contextService.setContextData(wetlands, data, this.context);
            }, (error) => { console.log(error); });
            layers.push(wetlandsPromise);
        }

        //state lands
        const stateLands = this.getWriteContextLocation(WellConfigurationIndex.ConditionStateLands, options);

        if (stateLands && stateLands !== "") {
            this.contextService.setContextData(stateLands, "", this.context);
            let promiseResponse = this.getQueryPromise(latLong, "stateLandsPromise", () => this.queryService.resolveQueryAtPoint(biweeklyUrl, "ODNR_LANDS", latLong));
            const stateLandsPromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);
                if (result && result.features && result.features.length > 0) {
                    this.contextService.setContextData(stateLands, this.stateLandsText, this.context);
                }
            }, (error) => { console.log(error); });

            layers.push(stateLandsPromise);
        }

        //Gas Storage Area
        const gasStorage = this.getWriteContextLocation(WellConfigurationIndex.ConditionGasStorage, options);

        if (gasStorage && gasStorage !== "") {
            this.contextService.setContextData(gasStorage, "", this.context);
            let promiseResponse = this.getQueryPromise(latLong, "gasStoragePromise",
                                                        () => this.queryService.resolveQueryAtPoint(yearlyUrl, "StorageFields_buffer_mi", latLong));
            const gasStoragePromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);
                if (result && result.features && result.features.length > 0) {
                    this.contextService.setContextData(gasStorage, this.gasStorageText, this.context);
                }
            }, (error) => { console.log(error); });

            layers.push(gasStoragePromise);
        }

        //Coal Bearing Townships
        const coalBearing = this.getWriteContextLocation(WellConfigurationIndex.ConditionCoalBearing, options);
        if (coalBearing && coalBearing !== "") {
            this.contextService.setContextData(coalBearing, "", this.context);
            let promiseResponse = this.getQueryPromise(latLong, "stateLandsPromise",
                                                                () => this.queryService.resolveQueryAtPoint(yearlyUrl, "CoalBearing_Townships", latLong));
            const coalBearingPromise = promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);
                if (result && result.features && result.features.length > 0) {
                    this.contextService.setContextData(coalBearing, 'true', this.context);
                }
            }, (error) => { console.log(error); });

            layers.push(coalBearingPromise);
        }

        return layers;
    }

    promiseState(latLong: number[], writeLocation: string): Promise<any> {
        let promiseResponse = this.getQueryPromise(latLong, "promiseState",
            () => this.locationService.getState(latLong[1], latLong[0]));

        return promiseResponse.promise.then((result) => {
            this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

            let state = "";
            if (result) {
                state = result;
            }
            this.contextService.setContextData(writeLocation, `${state}`, this.context);
        }, (error) => {
            console.log(`Error is getting state: ${error}`);
        });
    }

    promiseTownship(latLong: number[], options: GISOptions, config: GISConfig): Promise<any> {
        const townshipLoc = this.getWriteContextLocation(WellConfigurationIndex.TownshipName, options);
        if (townshipLoc) {
            let promiseResponse = this.getQueryPromise(latLong, "promiseTownship",
                () => this.queryService.resolveQueryAtPoint(config.yearlyGISDataUrl, "Townships", latLong, true));

            return promiseResponse.promise.then((result) => {
                this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

                if (result && result.features && result.features.length > 0) {
                    let data = result.features[0].attributes;
                    this.contextService.setContextData(this.getWriteContextLocation(WellConfigurationIndex.TownshipName, options), data["TOWNSHIP"] || "", this.context);
                }
            }, (error) => {
                console.log(`Error in getting township: ${error}`);
            });
        }
        return observableOf("").toPromise();
    }

    promiseUrbanization(latLong: number[], writeLocation: string): Promise<any> {
        let promiseResponse = this.getQueryPromise(latLong, "promiseUrbanization",
            () => this.locationService.getPopulationByCoordinate(latLong[1], latLong[0]));

        return promiseResponse.promise.then((result) => {
            this.addToCacheIfNecessary(promiseResponse.cacheKey, result);
			console.log('urbanization response', result);
            if (result && result["Population"] !== "ERROR") {
                const popResult = this.setUrbanization(result["Population"], result["ZoneType"]);
                this.contextService.setContextData(writeLocation, popResult, this.context);
            }
        },
        (error) => {
            console.log(`Error in getting urbanization: ${error}`);
        });
    }

    promiseWatershed(latLong: number[], writeLocation: string, config: GISConfig): Promise<any> {
        let promiseResponse = this.getQueryPromise(latLong, "promiseWatershed",
            () => this.queryService.queryLayerAtPoint_Unauthenticated(config.watershedServicesUrl, latLong));

        return promiseResponse.promise.then((result) => {
            this.addToCacheIfNecessary(promiseResponse.cacheKey, result);
            let watershed = "";
            if (result && result.features && result.features.length > 0) {
                if (isDefined(result.features[0]["attributes"]["huc6"])) {
                    if (result.features[0]["attributes"]["huc6"].startsWith("041")) {
                        watershed = "LAKEERIE";
                    } else {
                        watershed = "OHIORIVER";
                    }
                }
            }
            this.contextService.setContextData(writeLocation, watershed, this.context);
        }, (error) => {
            console.log(`Error in getting watershed: ${error}`);
        });
    }

    promiseWellheadVariance(latLong: number[], writeLocation: string, config: GISConfig): Promise<any> {
        let promiseResponse = this.getQueryPromise(latLong, "promiseWellheadVariance",
            () => this.queryService.resolveBufferQueryAtPoint(config.existingWellsGISLayer, "Well_Pts", latLong, 100));

        return promiseResponse.promise.then((result) => {
            this.addToCacheIfNecessary(promiseResponse.cacheKey, result);

            if (result && result.features && result.features.length > 0 &&  result.features[0].attributes !== "")
                this.contextService.setContextData(writeLocation, 'true', this.context);
            else
                this.contextService.setContextData(writeLocation, "", this.context);

        }, (error) => {
            console.log(`Error in getting wellhead variance: ${error}`);
        });
    }

    getContextTarget(idx: WellConfigurationIndex, options: GISOptions): string {
        let target = ""
        let foundIndex = options.readContextOptions.find(d => d.index === idx);
        if (foundIndex !== null && foundIndex !== undefined)
            target = foundIndex.location;

        return target;
    }

    public getReadContextLocation(location: WellConfigurationIndex, options: WellConfigurationData[]): string {
        let loc = ""
        let option = options.find(d => d.index === location)

        if (option !== null && option !== undefined)
            loc = option.location;

        if (loc && loc.length > 0) {
            loc = this.ensureLocEndsWithValue(loc);
        }
        return loc;
    }

    public getWriteContextLocation(location: WellConfigurationIndex, options: GISOptions): string {
        let loc = ""
        let option = options.writeContextOptions.find(d => d.index === location);

        if (option !== null && option !== undefined)
            loc = option.location;

        if (loc && loc.length > 0) {
            loc = this.ensureLocEndsWithValue(loc);
        }
        return loc;
    }

    public ensureLocEndsWithValue(location: string) {
        if (location.length < 6 || location.substring(location.length - 6, location.length) !== ".value") {
            location = `${location}.value`;
        }
        return location;
    }

    public setUrbanization(censusData: any, zoneType: string = ""): string {
        let popResult = "";
        var maxUrbanTypes = ["VILLAGE", "CITY"];

        //If the type of zone is vilage or city, it's always considered urban, and needs to trigger a fee of 1000
        //If it's a township, the fee and urban status varies depending on population count
        if (maxUrbanTypes.some(a => a == zoneType))
            popResult = "URBMCP";
        else {
            if (censusData === "NONURB") {
                popResult = "NONURB";
            } else if (zoneType == "TOWNSHIP"){
                const population = Number(censusData);
                if (population < 5000) {
                    popResult = "NONURB";
                } else if (population >= 5000 && population < 10000) {
                    popResult = "URB5000";
                } else if (population >= 10000 && population < 15000) {
                    popResult = "URB10000";
                } else if (population > 15000) {
                    popResult = "URBMCP";
                }
            }
        }

        return popResult;
    }
}
